import React, { PropsWithChildren } from 'react'
import { Query } from 'react-apollo'
import memoize from 'lodash.memoize'
import { RemoteComponent } from '@contrast-security-inc/design-system-foundations'
import { Grid, MaxCellHeightsByRow } from '../../legacy-design-system/grid'
import { GridLoader, GridStateManager } from '../grid-data'
import { sendDefaultEvent } from '../analytics'
import { LegacyGridContainer } from '../../legacy-design-system/legacy-main'
import { Order, OrderDirection } from '../../legacy-design-system/order'
import { AggregationConfigs, AggregationOptions } from '../aggregations'
import { AgentFeaturesLoader } from '../agent-features'
import { ApplicationLoaderCacheOnly } from '../application-loader'
import {
  OnGroupToggleProps,
  OnOptionToggleProps
} from '../../legacy-design-system/filter'
import { ApplicationOrderField, orderFieldConfig } from './order'
import {
  getVulnerabilityCellRenderer,
  loadingVulnerabilityCellRenderer
} from './vulnerability-cell'
import {
  getAttackStatusCellRenderer,
  loadingAttackStatusCellRenderer
} from './attack-status-cell'
import { getGradeCellRenderer, loadingGradeCellRenderer } from './grade-cell'
import {
  getApplicationMetadataCellRenderer,
  loadingApplicationMetadataCell
} from './application-metadata-cell'
import { BulkActionList } from './actions'
import { gradeHeaderRenderer } from './grade-header'
import { metadataHeaderRenderer } from './metadata-header'
import { getRowActionsCellRenderer } from './row-actions-cell'
import { Application, ApplicationAggregationCategory } from './shared'
import { CustomApplicationAggregation, MetadataFilter } from './index'
import {
  aggregationsQuery,
  quickFiltersQuery,
  collectionQuery,
  metadataQuery
} from './queries'
import { SplitContext } from '../../ng/splitio-wrapper'

const ApplicationsTable = React.lazy(
  // @ts-ignore
  () => import('platformApplications/ApplicationsTable')
)

const initialAggregationSelections: AggregationOptions<ApplicationAggregationCategory> =
  {
    scores: [],
    servers: [],
    languages: [],
    technologies: [],
    tags: [],
    compliance: [],
    openVulnSeverities: [],
    environment: [],
    appImportances: []
  }

export const aggregationConfigs: AggregationConfigs<ApplicationAggregationCategory> =
  {
    scores: {
      groupName: {
        id: 'title_score',
        defaultMessage: 'Score'
      },
      bucketToID: ({ key }) => key,
      bucketToString: ({ key }) => key
    },
    servers: {
      groupName: {
        id: 'title_servers',
        defaultMessage: 'Servers'
      },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { name } }) => name
    },
    languages: {
      groupName: {
        id: 'title_programmingLanguages',
        defaultMessage: 'Languages'
      },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    },
    technologies: {
      groupName: {
        id: 'title_technologies',
        defaultMessage: 'Technologies'
      }
    },
    tags: {
      groupName: {
        id: 'title_tags_application',
        defaultMessage: 'Tags'
      }
    },
    compliance: {
      groupName: {
        id: 'title_compliancePolicy',
        defaultMessage: 'Compliance Policy'
      },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { name } }) => name
    },
    openVulnSeverities: {
      groupName: {
        id: 'title_applications_openVulnSeverities'
      },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    },
    environment: {
      groupName: {
        id: 'title_environments'
      },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    },
    appImportances: {
      groupName: {
        id: 'title_appImportances'
      },
      bucketToID: ({ key: { id } }) => id,
      bucketToString: ({ key: { label } }) => label
    }
  }

function quickFilterToGroup({ id }) {
  switch (id) {
    case 'ARCHIVED':
      return 'archived'

    default:
      return 'other'
  }
}

interface ApplicationQueryProps {
  readonly rowDataID: string
  readonly children: (rowData: Application) => React.ReactNode
}

const getApplicationQuery = memoize(
  (organizationID) =>
    function ApplicationRowQuery({
      rowDataID: applicationID,
      children
    }: ApplicationQueryProps) {
      return (
        <ApplicationLoaderCacheOnly
          organizationID={organizationID}
          applicationID={applicationID}
          children={(application) => children(application)}
        />
      )
    }
)

const applicationBatchSize = 25

interface State {
  readonly customMetadataSelections: MetadataFilter[]
}

const metadataCellAggregations: ApplicationAggregationCategory[] = [
  'tags',
  'languages',
  'servers',
  'openVulnSeverities',
  'technologies',
  'environment',
  'appImportances'
]

export interface ContextValue {
  readonly order: Order<ApplicationOrderField>
  readonly filterText: string
  readonly quickFilterID: string
  readonly onSetSortField: (field: ApplicationOrderField) => void
  readonly onSetSortDirection: (direction: OrderDirection) => void
  readonly onFilterTextChange: (vaslue: string) => void
  readonly onQuickFilterIDChange: (quickFilterID: string) => void
  readonly onGroupToggle: (
    props: OnGroupToggleProps<ApplicationAggregationCategory>
  ) => void
  readonly onOptionToggle: (
    props: OnOptionToggleProps<ApplicationAggregationCategory>
  ) => void
  readonly aggregationSelections: AggregationOptions<ApplicationAggregationCategory>
  readonly onAggregationCategoriesClear: (
    categories: ApplicationAggregationCategory[]
  ) => void
  readonly updateCellHeight: (rowDataID: string, height: number) => void
  readonly registerListRef: (ref: any) => void
  readonly maxCellHeightsByRow: MaxCellHeightsByRow
  readonly recomputeRowHeights: () => void
  readonly customMetadataSelections: MetadataFilter[]
  readonly onMetadataOptionsToggle: (
    groupID: string,
    optionIDs: string[]
  ) => void
  readonly getOnAllAggregationCategoriesClear: (
    fn: (categories: ApplicationAggregationCategory[]) => void
  ) => (categories: ApplicationAggregationCategory[]) => void
  readonly onClearMetadataSelections: () => void
  readonly resetFilters: () => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const ApplicationGridContext = React.createContext<ContextValue>({
  order: {
    field: 'NAME',
    direction: orderFieldConfig.NAME.direction
  },
  onSetSortField: noop,
  onSetSortDirection: noop,
  filterText: '',
  onFilterTextChange: noop,
  quickFilterID: 'OPEN',
  onQuickFilterIDChange: noop,
  aggregationSelections: initialAggregationSelections,
  onGroupToggle: noop,
  onOptionToggle: noop,
  onAggregationCategoriesClear: noop,
  updateCellHeight: noop,
  registerListRef: noop,
  maxCellHeightsByRow: {},
  recomputeRowHeights: noop,
  customMetadataSelections: [],
  onMetadataOptionsToggle: noop,
  getOnAllAggregationCategoriesClear: () => noop,
  onClearMetadataSelections: noop,
  resetFilters: noop
})

export const appGridAreVariablesEqual = (var1, var2) => {
  if (var1.customMetadata && var2.customMetadata) {
    const var1CustomMetadata: MetadataFilter[] = var1.customMetadata
    const var2CustomMetadata: MetadataFilter[] = var2.customMetadata

    return (
      var1CustomMetadata.length === var2CustomMetadata.length &&
      var1CustomMetadata.every(
        ({ fieldID: var1FieldID, values: var1Values }) => {
          const var2Metadata = var2CustomMetadata.find(
            ({ fieldID: var2FieldID }) => var1FieldID === var2FieldID
          )

          return (
            !!var2Metadata &&
            var1Values.length === var2Metadata.values.length &&
            new Set([...var1Values, ...var2Metadata.values]).size ===
              var1Values.length
          )
        }
      )
    )
  }
  return true
}

export function ApplicationsGrid() {
  const isApplicationGroupingOn = React.useContext(
    SplitContext
  ).isSplitTreatmentOn('PROD-1916_application_grouping')

  if (isApplicationGroupingOn) {
    return (
      <RemoteComponent testId="remote-applications-table">
        <ApplicationsTable />
      </RemoteComponent>
    )
  }

  return (
    <AgentFeaturesLoader
      render={({ features }) => {
        return (
          <ApplicationGridContext.Consumer>
            {({
              order,
              onSetSortField,
              onSetSortDirection,
              filterText,
              onFilterTextChange,
              quickFilterID,
              onQuickFilterIDChange,
              aggregationSelections,
              onGroupToggle,
              onOptionToggle,
              onAggregationCategoriesClear,
              customMetadataSelections,
              onMetadataOptionsToggle,
              getOnAllAggregationCategoriesClear,
              onClearMetadataSelections
            }) => {
              return (
                <GridLoader<
                  Application,
                  ApplicationOrderField,
                  ApplicationAggregationCategory
                >
                  aggregationsQuery={aggregationsQuery}
                  aggregationsQueryRootKey="applicationAggregations"
                  quickFiltersQueryRootKey="applicationQuickFilters"
                  collectionQueryRootKey="applications"
                  collectionQuery={collectionQuery}
                  batchSize={applicationBatchSize}
                  quickFiltersQuery={quickFiltersQuery}
                  filterText={filterText}
                  order={order}
                  quickFilterID={quickFilterID}
                  aggregationBucketIDConfigs={aggregationConfigs}
                  aggregationFilters={aggregationSelections}
                  additionalFilters={
                    customMetadataSelections
                      ? { customMetadata: customMetadataSelections }
                      : {}
                  }
                  areVariablesEqual={appGridAreVariablesEqual}
                >
                  {({
                    totalRowCount,
                    loadMore,
                    refresh,
                    assessLicenseBreakdown,
                    organizationRole,
                    organizationID,
                    organizationType,
                    restrictedEditRole,
                    protect,
                    quickFilters,
                    aggregations,
                    refetchQueries: loaderRefetchQueries,
                    loadedRowCount,
                    rowDataValue,
                    rowGetter,
                    isRowLoaded
                  }) => {
                    const metadataQueryInfo = {
                      query: metadataQuery,
                      variables: Object.assign({}, aggregationSelections, {
                        organizationID,
                        filterText,
                        quickFilter: quickFilterID,
                        customMetadata: customMetadataSelections
                      })
                    }

                    const isFreemiumOrganization =
                      organizationType === 'Freemium'

                    const refetchQueries = [...loaderRefetchQueries]

                    if (!isFreemiumOrganization) {
                      refetchQueries.push(metadataQueryInfo)
                    }

                    return (
                      <Query<{
                        readonly customApplicationAggregations: CustomApplicationAggregation[]
                      }>
                        fetchPolicy="network-only"
                        query={metadataQueryInfo.query}
                        variables={metadataQueryInfo.variables}
                        skip={isFreemiumOrganization}
                      >
                        {({ data }) => {
                          const metadataAggregations =
                            (data && data.customApplicationAggregations) || []

                          const ApplicationQuery =
                            getApplicationQuery(organizationID)

                          return (
                            <LegacyGridContainer>
                              <Grid<
                                Application,
                                ApplicationOrderField,
                                ApplicationAggregationCategory
                              >
                                RowQuery={ApplicationQuery}
                                loadedRowCount={loadedRowCount}
                                rowDataValue={rowDataValue}
                                gridDataValue={assessLicenseBreakdown}
                                filterValue={{
                                  customMetadataSelections,
                                  metadataAggregations
                                }}
                                isRowLoaded={isRowLoaded}
                                rowGetter={rowGetter}
                                loadMore={loadMore}
                                totalRowCount={totalRowCount}
                                rowHeight={() => 70}
                                renderBulkActions={(rowSelectionProps) => (
                                  <BulkActionList
                                    customMetadataSelections={
                                      customMetadataSelections
                                    }
                                    organizationID={organizationID}
                                    refetchQueries={refetchQueries}
                                    refresh={refresh}
                                    organizationRole={organizationRole}
                                    isFreemium={isFreemiumOrganization}
                                    restrictedEditRole={restrictedEditRole}
                                    {...rowSelectionProps}
                                  />
                                )}
                                titleMessage={{
                                  id: 'title_applications',
                                  defaultMessage: 'Applications'
                                }}
                                searchPlaceholderMessage={{
                                  id: 'placeholder_findApplication',
                                  defaultMessage: 'Find Application'
                                }}
                                order={order}
                                onSetSortField={(value) => {
                                  onSetSortField(value)
                                  sendDefaultEvent(
                                    'applications.grid.sort',
                                    value
                                  )
                                }}
                                onSetSortDirection={onSetSortDirection}
                                orderFieldConfig={orderFieldConfig}
                                onFilterTextChange={(value) => {
                                  onFilterTextChange(value)
                                  sendDefaultEvent(
                                    'applications.grid.search',
                                    value
                                  )
                                }}
                                filterText={filterText}
                                onQuickFilterIDChange={onQuickFilterIDChange}
                                quickFilterID={quickFilterID}
                                quickFilters={quickFilters}
                                quickFilterToGroup={quickFilterToGroup}
                                aggregations={aggregations}
                                aggregationConfigs={aggregationConfigs}
                                aggregationSelections={aggregationSelections}
                                onGroupToggle={onGroupToggle}
                                onOptionToggle={onOptionToggle}
                                onAggregationCategoriesClear={getOnAllAggregationCategoriesClear(
                                  onAggregationCategoriesClear
                                )}
                                batchSize={applicationBatchSize}
                                columns={[
                                  {
                                    cellRenderer: getGradeCellRenderer({
                                      features
                                    }),
                                    loadingCellRenderer:
                                      loadingGradeCellRenderer,
                                    horizontalCenter: true,
                                    headerConfig: {
                                      message: {
                                        id: 'title_score',
                                        defaultMessage: 'Score'
                                      },
                                      render: gradeHeaderRenderer
                                    },
                                    width: 88,
                                    minWidth: 88,
                                    maxWidth: 88
                                  },
                                  {
                                    cellRenderer:
                                      getApplicationMetadataCellRenderer({
                                        organizationID,
                                        organizationRole,
                                        isFreemiumOrganization,
                                        features,
                                        hasLicenses:
                                          assessLicenseBreakdown.unused > 0,
                                        refetchQueries,
                                        refresh
                                      }),
                                    loadingCellRenderer:
                                      loadingApplicationMetadataCell,
                                    headerConfig: {
                                      message: {
                                        id: 'title_application'
                                      },
                                      aggregations: metadataCellAggregations,
                                      render: (props) =>
                                        metadataHeaderRenderer({
                                          ...props,
                                          customAggregations:
                                            metadataAggregations,
                                          customAggregationSelections:
                                            customMetadataSelections,
                                          onMetadataOptionsToggle,
                                          onClearMetadataSelections
                                        })
                                    },
                                    width: 400,
                                    minWidth: 230,
                                    grow: 5
                                  },
                                  {
                                    cellRenderer: getVulnerabilityCellRenderer({
                                      organizationID,
                                      features
                                    }),
                                    loadingCellRenderer:
                                      loadingVulnerabilityCellRenderer,
                                    headerConfig: {
                                      message: {
                                        id: 'title_openVulnerabilities',
                                        defaultMessage: 'Open Vulnerabilities'
                                      },
                                      aggregations: ['compliance']
                                    },
                                    width: 500,
                                    minWidth: 150,
                                    maxWidth: 550,
                                    grow: 5
                                  },
                                  {
                                    cellRenderer: getAttackStatusCellRenderer(),
                                    loadingCellRenderer:
                                      loadingAttackStatusCellRenderer,
                                    headerConfig: {
                                      message: {
                                        id: 'title_applications_attackStatus',
                                        defaultMessage: 'Attack Status'
                                      }
                                    },
                                    width: 100,
                                    minWidth: 120,
                                    horizontalCenter: true,
                                    isHidden: () => !protect
                                  },
                                  {
                                    cellRenderer: getRowActionsCellRenderer({
                                      organizationID,
                                      isFreemiumOrganization,
                                      restrictedEditRole,
                                      refresh,
                                      refetchQueries
                                    }),
                                    loadingCellRenderer: () => <div />,
                                    maxWidth: 110,
                                    width: 110,
                                    minWidth: 110
                                  }
                                ]}
                              />
                            </LegacyGridContainer>
                          )
                        }}
                      </Query>
                    )
                  }}
                </GridLoader>
              )
            }}
          </ApplicationGridContext.Consumer>
        )
      }}
    />
  )
}

export class ApplicationsGridProvider extends React.Component<
  PropsWithChildren<{}>,
  State
> {
  public state: State = {
    customMetadataSelections: []
  }

  public render() {
    const { customMetadataSelections } = this.state
    return (
      <GridStateManager
        defaultOrderField="NAME"
        orderFieldConfig={orderFieldConfig}
        filterType="applications"
      >
        {({ resetFilters, ...state }) => {
          return (
            <ApplicationGridContext.Provider
              value={{
                ...state,
                customMetadataSelections,
                onMetadataOptionsToggle: this.onMetadataOptionsToggle,
                getOnAllAggregationCategoriesClear:
                  this.getOnAllAggregationCategoriesClear,
                onClearMetadataSelections: this.onClearMetadataSelections,
                resetFilters: () => {
                  resetFilters()
                  this.onClearMetadataSelections()
                }
              }}
            >
              {this.props.children}
            </ApplicationGridContext.Provider>
          )
        }}
      </GridStateManager>
    )
  }

  private onMetadataOptionsToggle = (groupID: string, optionIDs: string[]) => {
    this.setState(({ customMetadataSelections: prevSelections }) => {
      const groupIndex = prevSelections.findIndex(
        ({ fieldID }) => fieldID === groupID
      )

      if (groupIndex > -1) {
        const selection = prevSelections[groupIndex]
        const updatedValues = optionIDs.reduce((acc, curr) => {
          const index = acc.indexOf(curr)
          if (index > -1) {
            return [...acc.slice(0, index), ...acc.slice(index + 1, acc.length)]
          }

          return [...acc, curr]
        }, selection.values)

        return {
          customMetadataSelections:
            updatedValues.length > 0
              ? [
                  ...prevSelections.slice(0, groupIndex),
                  {
                    fieldID: groupID,
                    values: updatedValues
                  },
                  ...prevSelections.slice(groupIndex + 1, prevSelections.length)
                ]
              : [
                  ...prevSelections.slice(0, groupIndex),
                  ...prevSelections.slice(groupIndex + 1, prevSelections.length)
                ]
        }
      }

      return {
        customMetadataSelections: [
          ...prevSelections,
          { fieldID: groupID, values: optionIDs }
        ]
      }
    })
  }

  private getOnAllAggregationCategoriesClear =
    (onAggregationCategoriesClear) =>
    (categories: ApplicationAggregationCategory[]) => {
      if (
        categories.every(
          (category) => metadataCellAggregations.indexOf(category) > -1
        )
      ) {
        this.onClearMetadataSelections()
      }
      onAggregationCategoriesClear(categories)
    }

  private onClearMetadataSelections = () => {
    this.setState({ customMetadataSelections: [] })
  }
}
